import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Hammer, ArrowRight, CheckCircle2, Star, Sparkles, DollarSign, Bot, MapPin, ShieldCheck,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import ApexLoader from "@/components/ApexLoader";

const SERVICES = [
  "Storm & wind damage",
  "Fire & smoke restoration",
  "Water mitigation & extraction",
  "Mold remediation",
  "Roofing & exterior",
  "Reconstruction / general contracting",
  "Appliance & mechanical leaks",
  "Sewage / biohazard",
];

const BENEFITS = [
  { Icon: DollarSign, t: "Free leads · zero per-lead fee", d: "Founding members get 12 months of routed leads at zero cost." },
  { Icon: Bot, t: "AI-drafted estimates", d: "Sentinel writes Xactimate-ready scopes from your photos." },
  { Icon: Sparkles, t: "Priority dispatch", d: "Storm hits your zip? You're at the top of the routing queue." },
  { Icon: ShieldCheck, t: "Verified-pro badge", d: "Lifetime founding badge on your contractor profile." },
];

export default function FoundingNetwork() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    state: "",
    primary_zip: "",
    services: [],
    crews_count: 1,
    years_active: 0,
    certifications: "",
    message: "",
  });
  const [state, setState] = useState({ loading: false, ok: false, error: "" });

  const toggleService = (s) => {
    setForm((f) => ({
      ...f,
      services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, ok: false, error: "" });
    try {
      await api.post("/contractor-application", form);
      setState({ loading: false, ok: true, error: "" });
      setForm({
        name: "", email: "", phone: "", company: "",
        state: "", primary_zip: "", services: [],
        crews_count: 1, years_active: 0, certifications: "", message: "",
      });
    } catch (err) {
      setState({ loading: false, ok: false, error: formatApiError(err.response?.data?.detail) || err.message });
    }
  };

  return (
    <section
      id="join-network"
      className="relative py-28 md:py-36 border-t border-white/5 overflow-hidden"
      data-testid="founding-network-section"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          background:
            "radial-gradient(900px 500px at 80% 20%, rgba(220,38,38,0.10), transparent 60%), radial-gradient(700px 400px at 10% 80%, rgba(255,255,255,0.03), transparent 60%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="block w-8 h-px bg-[#dc2626]" />
          <span className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-zinc-400">
            for restoration companies · founding network
          </span>
        </div>
        <div className="grid lg:grid-cols-2 gap-12 items-start mb-14">
          <div>
            <h2 className="font-display text-5xl md:text-6xl text-white tracking-tight leading-[0.95] mb-6">
              Be one of the <span className="silver-text italic">first 100</span>.
            </h2>
            <p className="font-body text-base text-zinc-300 leading-relaxed mb-6">
              We're handpicking 100 restoration companies to anchor the Apex network — the operators
              who'll get every lead, every storm warning, and every AI advantage before anyone else.
            </p>
            <p className="font-body text-base text-zinc-400 leading-relaxed">
              Storm season doesn't care if your CRM is ready. We do. Apply below and our team will
              reach out to walk you through onboarding before public launch.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {BENEFITS.map((b) => (
              <div key={b.t} className="border border-white/5 bg-[#121214] p-5">
                <b.Icon size={20} className="text-[#dc2626] mb-3" strokeWidth={1.5} />
                <div className="font-body text-sm text-white mb-1.5 font-medium">{b.t}</div>
                <div className="font-mono-apex text-[10px] text-zinc-500 leading-relaxed">{b.d}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Application form */}
        <div className="bg-[#121214] border border-white/10 p-8 md:p-10" data-testid="application-form-card">
          <div className="flex items-center gap-3 mb-8">
            <Hammer size={20} className="text-[#dc2626]" />
            <div>
              <div className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-zinc-500">application</div>
              <h3 className="font-display text-2xl text-white">Join the founding network</h3>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="contractor-application-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Your name</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="input-apex w-full px-4 py-3 font-body text-sm"
                  data-testid="app-name-input"
                />
              </div>
              <div>
                <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Company name</label>
                <input
                  required
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="input-apex w-full px-4 py-3 font-body text-sm"
                  data-testid="app-company-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Email</label>
                <input
                  required type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="input-apex w-full px-4 py-3 font-body text-sm"
                  data-testid="app-email-input"
                />
              </div>
              <div>
                <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Phone</label>
                <input
                  required type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="input-apex w-full px-4 py-3 font-body text-sm"
                  data-testid="app-phone-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">State</label>
                <input
                  required maxLength={2}
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })}
                  placeholder="TX"
                  className="input-apex w-full px-4 py-3 font-body text-sm uppercase"
                  data-testid="app-state-input"
                />
              </div>
              <div>
                <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Primary ZIP</label>
                <input
                  required
                  value={form.primary_zip}
                  onChange={(e) => setForm({ ...form, primary_zip: e.target.value })}
                  inputMode="numeric"
                  className="input-apex w-full px-4 py-3 font-body text-sm"
                  data-testid="app-zip-input"
                />
              </div>
              <div>
                <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Crews</label>
                <input
                  type="number" min={1}
                  value={form.crews_count}
                  onChange={(e) => setForm({ ...form, crews_count: parseInt(e.target.value) || 1 })}
                  className="input-apex w-full px-4 py-3 font-body text-sm"
                  data-testid="app-crews-input"
                />
              </div>
              <div>
                <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Years active</label>
                <input
                  type="number" min={0}
                  value={form.years_active}
                  onChange={(e) => setForm({ ...form, years_active: parseInt(e.target.value) || 0 })}
                  className="input-apex w-full px-4 py-3 font-body text-sm"
                  data-testid="app-years-input"
                />
              </div>
            </div>

            <div>
              <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-3">Services you offer</label>
              <div className="flex flex-wrap gap-2">
                {SERVICES.map((s) => {
                  const active = form.services.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => toggleService(s)}
                      className={`px-3 py-2 border font-mono-apex text-[10px] uppercase tracking-[0.15em] transition-colors ${
                        active
                          ? "border-[#dc2626] bg-[#dc2626]/15 text-[#fca5a5]"
                          : "border-white/10 bg-[#0a0a0b] text-zinc-300 hover:border-white/25"
                      }`}
                      data-testid={`service-${s.slice(0, 12).replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                    >
                      {active && "✓ "}{s}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
                Certifications (optional)
              </label>
              <input
                value={form.certifications}
                onChange={(e) => setForm({ ...form, certifications: e.target.value })}
                placeholder="IICRC, RIA, EPA RRP, HAAG…"
                className="input-apex w-full px-4 py-3 font-body text-sm"
                data-testid="app-certifications-input"
              />
            </div>

            <div>
              <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
                Anything else we should know? (optional)
              </label>
              <textarea
                rows={3}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Years of storm experience, biggest project, what you're hoping Apex unlocks for you…"
                className="input-apex w-full px-4 py-3 font-body text-sm resize-none"
                data-testid="app-message-input"
              />
            </div>

            {state.error && (
              <div className="font-mono-apex text-xs text-[#dc2626]" data-testid="app-error">{state.error}</div>
            )}
            {state.ok && (
              <div className="border-l-2 border-emerald-500 bg-emerald-500/10 p-4" data-testid="app-success">
                <div className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-emerald-300 flex items-center gap-2 mb-1">
                  <CheckCircle2 size={12} /> application received
                </div>
                <p className="font-body text-sm text-white">
                  Thank you. Our team will reach out within 2 business days. Welcome to the founding cohort.
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={state.loading}
              className="w-full btn-primary-apex px-6 py-4 font-mono-apex text-[11px] uppercase tracking-[0.25em] disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="app-submit-btn"
            >
              {state.loading ? <ApexLoader inline size={14} /> : <ArrowRight size={14} />}
              {state.loading ? "Submitting…" : "Apply to join the network"}
            </button>

            <p className="font-mono-apex text-[10px] text-zinc-600 leading-relaxed">
              Founding cohort closes when we hit 100 verified pros. Selection is rolling.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
